import { useState, type ReactNode } from 'react';
import { QUESTION_IMPORT_MAX_ROWS, type QuestionImportProblem } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Drawer } from '../../../components/Drawer';
import { DropZone } from '../../../components/DropZone';
import { Field } from '../../../components/Field';
import { Select } from '../../../components/Select';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { QUESTION_TEMPLATE_URL, useCheckQuestionFile, useImportQuestionFile } from '../api/assessments-api';
import styles from './Assessments.module.css';

/**
 * F9.17 — questions from a spreadsheet (ADR 0047). Choose a file and its rows are checked at
 * once; the good ones are imported and the bad ones listed by row (DECISIONS-LOG 2026-10-16 Q1).
 */

const ACCEPT = '.xlsx,.csv';

/** "Row 5 was skipped…" or "Rows 5, 9 were skipped…"; nothing when every row went in. */
function skippedNote(rows: readonly number[]): string | undefined {
  if (rows.length === 0) return undefined;
  if (rows.length === 1) return `Row ${rows[0]} was skipped. Fix it and upload just that row.`;
  return `Rows ${rows.join(', ')} were skipped. Fix them and upload just those rows.`;
}

interface QuestionImportProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly ventures: readonly { readonly id: string; readonly name: string }[];
  readonly defaultVentureId: string;
}

interface Checked {
  readonly file: File;
  readonly ready: number;
  readonly problems: readonly QuestionImportProblem[];
}

export function QuestionImport({ isOpen, onClose, ventures, defaultVentureId }: QuestionImportProps): ReactNode {
  const [ventureId, setVentureId] = useState(defaultVentureId);
  const [checked, setChecked] = useState<Checked | null>(null);
  const [error, setError] = useState<string | null>(null);
  const check = useCheckQuestionFile();
  const importFile = useImportQuestionFile();
  const show = useToasts((state) => state.show);

  const chosenVenture = ventureId === '' ? defaultVentureId : ventureId;

  function close(): void {
    setChecked(null);
    setError(null);
    onClose();
  }

  async function onFile(file: File): Promise<void> {
    setError(null);
    setChecked(null);
    try {
      const result = await check.mutateAsync(file);
      setChecked({ file, ready: result.questions.length, problems: result.problems });
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  async function onImport(): Promise<void> {
    if (!checked) return;
    try {
      const result = await importFile.mutateAsync({ file: checked.file, ventureId: chosenVenture });
      show({
        tone: 'success',
        message: `${result.imported} ${result.imported === 1 ? 'question' : 'questions'} added to the bank`,
        detail: skippedNote(result.problems.map((problem) => problem.row)),
      });
      close();
    } catch (cause) {
      show({ tone: 'danger', message: "The questions weren't imported", detail: errorMessage(cause) });
    }
  }

  const ready = checked?.ready ?? 0;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={close}
      title="Import questions"
      meta="From an Excel or CSV file, one question per row"
      footer={
        <div className={styles.drawerFooter}>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button variant="solid" disabled={ready === 0} isPending={importFile.isPending} onClick={() => void onImport()}>
            {ready === 0 ? 'Import' : `Import ${ready} ${ready === 1 ? 'question' : 'questions'}`}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        <p className={styles.hint}>
          Start from the template: it has the columns, two example rows to replace, and a sheet explaining each column. Up to {QUESTION_IMPORT_MAX_ROWS} questions per file.{' '}
          <a className={styles.templateLink} href={QUESTION_TEMPLATE_URL} download>
            Download the template
          </a>
        </p>
        {ventures.length > 1 && (
          <Field label="Venture" isRequired>
            {({ inputId }) => (
              <Select id={inputId} label="Venture" value={chosenVenture} options={ventures.map((venture) => ({ value: venture.id, label: venture.name }))} onChange={setVentureId} />
            )}
          </Field>
        )}
        <DropZone
          label="Choose the questions file"
          title="Drop the filled-in template here"
          hint="Excel (.xlsx) or CSV"
          accept={ACCEPT}
          isPending={check.isPending}
          pendingText="Checking the rows…"
          error={error}
          onFile={(file) => void onFile(file)}
        />
        {checked && (
          <>
            <p className={styles.summary}>
              {checked.file.name}: {ready} ready
              {checked.problems.length > 0 && ` · ${checked.problems.length} ${checked.problems.length === 1 ? 'needs' : 'need'} fixing`}
            </p>
            {checked.problems.length > 0 && (
              <>
                <p className={styles.hint}>These rows are skipped. The rest are imported; fix these in the file and upload just them afterwards.</p>
                <ul className={styles.importProblems}>
                  {checked.problems.map((problem) => (
                    <li key={problem.row} className={styles.importProblem}>
                      <span className={styles.importProblemRow}>Row {problem.row}</span> — {problem.message}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
